/**
 * useModelSelection
 *
 * Manages selectedModel + referenceSlots state + derived ratio/resolution options.
 * Resets ratio/resolution form values on model change if unsupported.
 * Max 50 lines.
 */
import { useState } from 'react';
import type { StudioModel } from '../ModelSelector';
import type { ReferenceSlot } from '../GenerationFormV2';

const DEFAULT_RATIOS = ['1:1', '4:3', '3:4', '16:9', '9:16'];
const DEFAULT_RESOLUTIONS = ['1k', '2k', '4k'];
const EMPTY_SLOTS: ReferenceSlot[] = [
  { id: null, previewUrl: null }, { id: null, previewUrl: null },
  { id: null, previewUrl: null }, { id: null, previewUrl: null },
];

export function useModelSelection(setValue: (field: 'ratio' | 'resolution', val: string) => void, ratio: string, resolution: string) {
  const [selectedModel, setSelectedModel] = useState<StudioModel | null>(null);
  const [referenceSlots, setReferenceSlots] = useState<ReferenceSlot[]>(EMPTY_SLOTS);

  const handleModelChange = (model: StudioModel) => {
    setSelectedModel(model);
    if (!model.config.supported_aspect_ratios.includes(ratio)) setValue('ratio', '1:1');
    if (!model.config.supported_resolutions.includes(resolution)) setValue('resolution', '1k');
  };

  const supportedRatios = selectedModel?.config.supported_aspect_ratios ?? DEFAULT_RATIOS;
  const supportedResolutions = selectedModel?.config.supported_resolutions ?? DEFAULT_RESOLUTIONS;
  const ratioOptions = supportedRatios.map(r => ({ value: r, label: r }));
  const resolutionOptions = supportedResolutions.map(r => ({ value: r, label: r.toUpperCase() }));

  return { selectedModel, handleModelChange, ratioOptions, resolutionOptions, referenceSlots, setReferenceSlots };
}
